import { useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { CheckCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/AppContext';

interface OrderConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const translations = {
  ko: {
    orderConfirmed: '주문 확인',
    orderPlaced: '주문이 접수되었습니다',
    returningToMenu: '메뉴로 돌아갑니다...'
  },
  es: {
    orderConfirmed: 'Pedido Confirmado',
    orderPlaced: 'Su pedido ha sido recibido',
    returningToMenu: 'Volviendo al menú...'
  },
  en: {
    orderConfirmed: 'Order Confirmed',
    orderPlaced: 'Your order has been placed',
    returningToMenu: 'Returning to menu...'
  }
};

export function OrderConfirmationModal({ isOpen, onClose }: OrderConfirmationModalProps) {
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    if (isOpen) {
      // 3초 후 자동으로 닫기
      const timer = setTimeout(() => {
        onClose();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[min(85vw,340px)] bg-background border-none rounded-3xl p-0 overflow-hidden">
        <div className="relative p-[min(8vw,3rem)] flex flex-col items-center justify-center">
          {/* Animated Circle Background */}
          <div className="relative mb-6">
            {/* Outer pulsing circle */}
            <div className="absolute inset-0 -m-8 rounded-full bg-primary/10 animate-ping" />

            {/* Middle circle */}
            <div className="absolute inset-0 -m-4 rounded-full bg-primary/20 animate-pulse" />

            {/* Inner circle with check icon */}
            <div className="relative w-24 h-24 rounded-full bg-primary flex items-center justify-center shadow-2xl shadow-primary/50 animate-scaleIn">
              {/* Animated checkmark */}
              <CheckCircle className="h-14 w-14 text-white animate-scaleIn" />
            </div>
          </div>

          {/* Text Content */}
          <div className="text-center space-y-3">
            <h2 className="text-[clamp(18px,5vw,24px)] font-bold text-foreground animate-fadeInUp">
              {t.orderConfirmed}
            </h2>
            <p className="text-[clamp(14px,3.5vw,16px)] text-muted-foreground animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
              {t.orderPlaced}
            </p>
            <p className="text-[clamp(12px,3vw,14px)] text-primary font-medium animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
              {t.returningToMenu}
            </p>
          </div>

          {/* Loading dots */}
          <div className="flex gap-2 mt-6 animate-fadeInUp" style={{ animationDelay: '0.6s' }}>
            <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0s' }} />
            <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0.2s' }} />
            <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0.4s' }} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}